import {
  AddressInputConfig,
  ComponentConfig,
  DropdownConfig,
  GridTableConfig,
  LabeledGridTableConfig,
  NoteWidgetConfig,
  PartnerInputConfig,
  StandardTableConfig
} from '@st/ui-config'
import { Box, Column, Expand, Note, Select, Space, Text } from './document-elements'
import {
  CheckboxInput,
  FIELD_HEIGHT,
  FormNameInput,
  FormTable,
  FormTextInput,
  Label,
  LabelPosition,
  MultiSelectInput,
  MultiSelectOption,
  RadioOption,
  RadioSelectInput,
  SectionRow,
  YesNoRadio
} from './form'
import { PARTNER_TYPE_OPTIONS, SHAREHOLDER_TYPE_OPTIONS } from './config'
import type { FormationFieldChange, FormationPageFields } from './render-data'

export function FormationComponent({
  config,
  fields,
  onFieldChange
}: {
  config: ComponentConfig
  fields: FormationPageFields
  onFieldChange: FormationFieldChange
}) {
  switch (config.__typename) {
    case 'TextInputConfig':
    case 'MoneyInputConfig':
      return (
        <FormTextInput
          label={config.label}
          inputKey={config.userInputKey}
          shouldShowHint={config.shouldShowPrevYearHint ?? false}
          fields={fields}
          onFieldChange={onFieldChange}
          mask={
            config.__typename == 'TextInputConfig' && config.inputFormat == 'SocialSecurityNumber'
          }
          multiline={(config.__typename == 'TextInputConfig' && config.isMultiline) ?? false}
        />
      )
    case 'DateInputConfig':
      return (
        <FormTextInput
          label={config.label}
          inputKey={config.userInputKey}
          fields={fields}
          onFieldChange={onFieldChange}
        />
      )
    case 'NameInputConfig':
      return (
        <FormNameInput
          userInputKey={config.userInputKey}
          fields={fields}
          onFieldChange={onFieldChange}
        />
      )
    case 'YesNoConfig':
      return (
        <YesNoRadio
          label={config.label ?? ''}
          labelPosition={(config.labelPosition ?? 'left') as LabelPosition}
          userInputKey={config.userInputKey}
          fields={fields}
          onFieldChange={onFieldChange}
        />
      )
    case 'FormSelectCategoryConfig':
    case 'CheckboxConfig':
      return (
        <CheckboxInput
          label={config.label ?? ''}
          userInputKey={config.userInputKey}
          fields={fields}
          onFieldChange={onFieldChange}
        />
      )
    case 'TextConfig':
      return <Label bold={config.isBold ?? false}>{config.content}</Label>
    case 'FilesInputConfig':
      return <Space />
    case 'RadioSelectConfig':
      const radioOptions: RadioOption[] = config.options.map((opt) => {
        return { label: opt.label, value: opt.key }
      })
      return (
        <RadioSelectInput
          label={config.label ?? ''}
          userInputKey={config.userInputKey}
          options={radioOptions}
          fields={fields}
          onFieldChange={onFieldChange}
        />
      )
    case 'MultiSelectConfig':
      const multiSelectOptions: MultiSelectOption[] = config.options.map((opt) => {
        return { label: opt.label, value: opt.key }
      })
      return (
        <MultiSelectInput
          label={config.label ?? undefined}
          userInputKey={config.userInputKey}
          options={multiSelectOptions}
          fields={fields}
          onFieldChange={onFieldChange}
        />
      )
    case 'AddressInputConfig':
      return <AddressInput {...config} fields={fields} onFieldChange={onFieldChange} />
    case 'StandardTableConfig':
      return <StandardTable {...config} fields={fields} onFieldChange={onFieldChange} />
    case 'GridTableConfig':
      return <GridTableInput {...config} fields={fields} onFieldChange={onFieldChange} />
    case 'LabeledGridTableConfig':
      return <LabeledGridTableInput {...config} fields={fields} onFieldChange={onFieldChange} />
    case 'NoteWidgetConfig':
      return <FormationNote {...config} fields={fields} />
    case 'PartnerInputConfig':
      return <PartnerInput {...config} fields={fields} onFieldChange={onFieldChange} />
    case 'DropdownConfig':
      return <DropdownInput {...config} fields={fields} onFieldChange={onFieldChange} />
    default:
      return <Label>{config.__typename ?? ''}</Label>
  }
}

function DropdownInput(
  config: DropdownConfig & {
    fields: FormationPageFields
    onFieldChange: FormationFieldChange
  }
) {
  type Option = { value: string; label: string }

  const options: Option[] = config.options.map((option) => ({
    value: option.key,
    label: option.label
  }))

  return (
    <FormSelect
      userInputKey={config.userInputKey}
      placeholder={config.label}
      ariaLabel={config.label ?? 'Select an option'}
      options={options}
      fields={config.fields}
      onFieldChange={config.onFieldChange}
    />
  )
}

function AddressInput({
  userInputKey: key,
  fields,
  onFieldChange
}: AddressInputConfig & {
  fields: FormationPageFields
  onFieldChange: FormationFieldChange
}) {
  return (
    <SectionRow>
      <Expand flex={16}>
        <FormTextInput
          fontSize={9}
          label="Addr."
          inputKey={`${key}.street`}
          fields={fields}
          onFieldChange={onFieldChange}
        />
      </Expand>
      <Space w={8} />
      <Expand flex={8}>
        <FormTextInput
          fontSize={9}
          label="City"
          inputKey={`${key}.city`}
          fields={fields}
          onFieldChange={onFieldChange}
        />
      </Expand>
      <Space w={8} />
      <Expand flex={8}>
        <FormTextInput
          fontSize={9}
          label="State"
          inputKey={`${key}.state`}
          fields={fields}
          onFieldChange={onFieldChange}
        />
      </Expand>
      <Space w={8} />
      <Expand flex={6}>
        <FormTextInput
          fontSize={9}
          label="Country"
          inputKey={`${key}.country`}
          fields={fields}
          onFieldChange={onFieldChange}
        />
      </Expand>
      <Space w={8} />
      <Expand flex={5}>
        <FormTextInput
          fontSize={9}
          label="Zip"
          inputKey={`${key}.zip`}
          fields={fields}
          onFieldChange={onFieldChange}
        />
      </Expand>
    </SectionRow>
  )
}

function PartnerInput({
  userInputKey: key,
  fields,
  onFieldChange
}: PartnerInputConfig & {
  fields: FormationPageFields
  onFieldChange: FormationFieldChange
}) {
  return (
    <SectionRow minHeight={21}>
      <Expand flex={6}>
        <FormSelect
          userInputKey={`${key}.type`}
          placeholder="Select type"
          options={PARTNER_TYPE_OPTIONS.map((o) => {
            return { value: o.key, label: o.label }
          })}
          fields={fields}
          onFieldChange={onFieldChange}
        />
      </Expand>
      <Space w={8} />
      <Expand flex={8}>
        <FormNameInput
          userInputKey={`${key}.name`}
          fields={fields}
          onFieldChange={onFieldChange}
        />
      </Expand>
      <Space w={8} />
      <Expand flex={8}>
        <FormTextInput
          fontSize={9}
          label="Entity"
          inputKey={`${key}.entity_name`}
          fields={fields}
          onFieldChange={onFieldChange}
        />
      </Expand>
      <Space w={8} />
      <Expand flex={5}>
        <FormTextInput
          fontSize={9}
          label="TIN"
          inputKey={`${key}.tin`}
          fields={fields}
          onFieldChange={onFieldChange}
        />
      </Expand>
    </SectionRow>
  )
}

function FormSelect({
  userInputKey,
  placeholder,
  ariaLabel,
  options,
  fields,
  onFieldChange
}: {
  userInputKey: string
  placeholder?: string
  ariaLabel?: string
  options: Array<{ value: string; label: string }>
  fields: FormationPageFields
  onFieldChange: FormationFieldChange
}) {
  const value = toStr(fields[userInputKey]?.value)

  return (
    <Box height={FIELD_HEIGHT}>
      <Select
        name={userInputKey}
        value={value}
        placeholder={placeholder}
        aria-label={ariaLabel}
        options={options}
        onChange={(value) => {
          onFieldChange(userInputKey, value)
        }}
      />
    </Box>
  )
}

function StandardTable({
  columns,
  fields,
  onFieldChange
}: StandardTableConfig & {
  fields: FormationPageFields
  onFieldChange: FormationFieldChange
}) {
  return (
    <FormTable
      colCount={columns.length}
      rowCount={columns[0].components.length}
      colHeader={(colIndex) => columns[colIndex].title}
    >
      {(rowIndex, colIndex) => {
        const config = columns[colIndex].components[rowIndex]
        return (
          <FormationComponent config={config} fields={fields} onFieldChange={onFieldChange} />
        )
      }}
    </FormTable>
  )
}

/**
 * A grid-style input where there the first row is labels
 * Then there is one or more entries for those labels
 *
 * For example a users table might look like:
 * +---------------------------+
 * | Name | Age | Address      |
 * +---------------------------+
 * | Bob  | 23  | 123 Blu Ln   |
 * | Doe  | 99  | 77 Severn    |
 * | ...  | ..  | ...          |
 * +---------------------------+
 *
 * The key structure might look like
 * registered_users.0.name
 * registered_users.1.age
 *
 * The key is constructed like $userInputKey.$rowIndex.$fieldName
 *
 * @param
 * @returns
 */
function GridTableInput({
  userInputKey,
  columns,
  rowCount,
  fields,
  onFieldChange
}: GridTableConfig & {
  fields: FormationPageFields
  onFieldChange: FormationFieldChange
}) {
  return (
    <FormTable
      colCount={columns.length}
      rowCount={rowCount}
      colHeader={(colIndex) => columns[colIndex].title}
      colFlex={(colIndex) => columns[colIndex].width ?? 1}
    >
      {(rowIndex, colIndex) => (
        <FormTextInput
          inputKey={`${userInputKey}.${rowIndex}.${columns[colIndex].key}`}
          fields={fields}
          onFieldChange={onFieldChange}
        />
      )}
    </FormTable>
  )
}

function LabeledGridTableInput({
  userInputKey,
  columns,
  rows,
  fields,
  onFieldChange
}: LabeledGridTableConfig & {
  fields: FormationPageFields
  onFieldChange: FormationFieldChange
}) {
  return (
    <FormTable
      rowCount={rows.length}
      colCount={columns.length}
      colHeader={(colIndex) => columns[colIndex].title}
      rowHeader={(rowIndex) => rows[rowIndex].title}
    >
      {(rowIndex, colIndex) => {
        const column = columns[colIndex]
        const row = rows[rowIndex]
        return (
          <FormTextInput
            inputKey={`${userInputKey}.${row.key}.${column.key}`}
            fields={fields}
            onFieldChange={onFieldChange}
          />
        )
      }}
    </FormTable>
  )
}

function FormationNote({
  userInputKey,
  fields
}: NoteWidgetConfig & { fields: FormationPageFields }) {
  const comment = fields[userInputKey]?.comment

  if (!comment) return null
  return <Note id={comment.key} author={comment.author} time={comment.time} body={comment.body} />
}

function toStr(value: any) {
  return typeof value === 'string' ? value : ''
}
